/**
 * Position Strategy Detector Service
 *
 * Analyzes existing positions to detect multi-leg option strategies
 * like spreads, iron condors, straddles, strangles, etc.
 */

import { parseOptionSymbol } from "@/utils/option-symbol-utils";
import type { EnhancedPosition } from "@/types/position.types";
import type {
  StrategyMetadata as BaseStrategyMetadata,
  StrategySource,
} from "@/types/strategy-metadata.types";

interface PositionAttributeEntry {
  attribute_type: string;
  attribute_value: unknown;
}

export const StrategyType = {
  SINGLE_LEG: "single_leg",
  COVERED_CALL: "covered_call",
  CASH_SECURED_PUT: "cash_secured_put",
  VERTICAL_SPREAD: "vertical_spread",
  IRON_CONDOR: "iron_condor",
  IRON_BUTTERFLY: "iron_butterfly",
  CALL_BUTTERFLY: "call_butterfly",
  PUT_BUTTERFLY: "put_butterfly",
  STRADDLE: "straddle",
  STRANGLE: "strangle",
  CALENDAR_SPREAD: "calendar_spread",
  DIAGONAL_SPREAD: "diagonal_spread",
  RATIO_SPREAD: "ratio_spread",
  COLLAR: "collar",
  BUTTERFLY: "butterfly",
  CONDOR: "condor",
  CUSTOM: "custom",
} as const;

export type StrategyKind = (typeof StrategyType)[keyof typeof StrategyType];

export const LegRole = {
  LONG_STOCK: "long_stock",
  SHORT_STOCK: "short_stock",
  LONG_CALL: "long_call",
  SHORT_CALL: "short_call",
  LONG_PUT: "long_put",
  SHORT_PUT: "short_put",
  PROTECTIVE_PUT: "protective_put",
  COVERED_CALL: "covered_call",
} as const;

export type LegRoleValue = (typeof LegRole)[keyof typeof LegRole];

type StrategyDetectionSource =
  | "complex_order"
  | "auto_detected"
  | "user_defined"
  | "tagged";

export type StrategyCategory =
  | "defined_risk"
  | "undefined_risk"
  | "stock"
  | "income"
  | "custom";

export interface StrategyLeg {
  position: EnhancedPosition;
  role: LegRoleValue; // e.g., "long_call", "short_put", etc.
  strike: number;
  optionType: "C" | "P";
  quantity: number;
}

export interface DetectedStrategyMetadata
  extends BaseStrategyMetadata {
  detectionSource: StrategyDetectionSource;
  strategyCategory: StrategyCategory;
  isUserModified: boolean;
  originalName?: string;
  complexOrderId?: string;
  orderDescription?: string;
  sourceStrategyId?: string;
  sourceStrategyName?: string;
  detectionConfidence: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface DetectedStrategy {
  id: string;
  type: StrategyKind;
  name: string; // Display name for the strategy
  underlyingSymbol: string;
  legs: StrategyLeg[];
  netPremium: number;
  netDelta: number;
  maxProfit?: number;
  maxLoss?: number;
  breakevens?: number[];
  expirationDate: string;
  daysToExpiration: number;
  confidence: number; // 0-1 confidence score that this is the intended strategy
  metadata: DetectedStrategyMetadata;
}

// User override interfaces
export interface UserStrategyOverrides {
  [accountNumber: string]: {
    [strategyId: string]: StrategyOverride;
  };
}

export interface StrategyOverride {
  customName?: string;
  excludedPositions: string[]; // Position symbols to remove from strategy
  addedPositions: string[]; // Position symbols to add to strategy
  isDeleted?: boolean; // Strategy completely deleted by user
  updatedAt: Date;
}

// Strategy edit actions interface
export interface StrategyEditActions {
  renameStrategy(strategyId: string, newName: string): void;
  removeFromStrategy(strategyId: string, positionSymbol: string): void;
  createCustomStrategy(
    positionSymbols: string[],
    name?: string,
  ): DetectedStrategy;
  addToStrategy(strategyId: string, positionSymbol: string): void;
  deleteStrategy(strategyId: string): void;
  resetToAutomatic(strategyId: string): void;
}

export class PositionStrategyDetector implements StrategyEditActions {
  private userOverrides: UserStrategyOverrides = {};
  private strategies: DetectedStrategy[] = [];

  constructor() {
    this.loadUserOverrides();
  }

  /**
   * Detect all strategies in a list of positions with user overrides applied
   */
  detectStrategies(
    positions: EnhancedPosition[],
    positionAttributes?: Map<string, PositionAttributeEntry[]>,
  ): DetectedStrategy[] {

    // STEP 0: Detect tagged strategies first (highest confidence)
    const taggedStrategies = this.detectTaggedStrategies(
      positions,
      positionAttributes,
    );

    // STEP 1: Group by complex-order-id (100% confidence)
    const remainingAfterTagged = this.getUnusedPositions(
      positions,
      taggedStrategies,
    );
    const complexOrderStrategies =
      this.detectFromComplexOrders(remainingAfterTagged);

    // STEP 2: Get positions not used in tagged or complex orders
    const usedPositionSymbols = new Set(
      complexOrderStrategies.flatMap((s) =>
        s.legs.map((l) => l.position.symbol),
      ),
    );
    const ungroupedPositions = positions.filter(
      (p) => !usedPositionSymbols.has(p.symbol),
    );

    // STEP 3: Pattern detection for remaining positions
    const patternStrategies = this.detectFromPatterns(ungroupedPositions);

    // STEP 4: Combine all strategies
    const allStrategies = [
      ...taggedStrategies,
      ...complexOrderStrategies,
      ...patternStrategies,
    ];

    // STEP 5: Apply user overrides
    const finalStrategies = this.applyUserOverrides(allStrategies, positions);

    // Store for edit operations
    this.strategies = finalStrategies;

    return finalStrategies;
  }

  /**
   * Detect strategies from TastyTrade complex orders (100% confidence)
   */
  private detectFromComplexOrders(
    positions: EnhancedPosition[],
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];
    const complexOrderGroups = new Map<string, EnhancedPosition[]>();


    // Group positions by complex-order-id
    for (const position of positions) {
      const complexOrderId = position["complex-order-id"];

      if (complexOrderId) {
        if (!complexOrderGroups.has(complexOrderId)) {
          complexOrderGroups.set(complexOrderId, []);
        }
        complexOrderGroups.get(complexOrderId)!.push(position);
      }
    }


    // Create strategies from complex order groups
    for (const [complexOrderId, groupPositions] of complexOrderGroups) {
      if (groupPositions.length >= 2) {
        // Must have at least 2 legs
        const strategy = this.createStrategyFromComplexOrder(
          complexOrderId,
          groupPositions,
        );
        strategies.push(strategy);
      }
    }

    return strategies;
  }

  /**
   * Pattern-based detection for positions without complex-order-id
   */
  private detectFromPatterns(
    positions: EnhancedPosition[],
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];
    const usedPositions = new Set<string>();

    // Group positions by underlying symbol
    const byUnderlying = this.groupByUnderlying(positions);

    for (const underlyingPositions of byUnderlying.values()) {
      // Skip if only one position (can't form a multi-leg strategy)
      if (underlyingPositions.length < 2) continue;

      // Separate stocks and options
      const stocks = underlyingPositions.filter(
        (p) => p["instrument-type"] === "Equity",
      );
      const options = underlyingPositions.filter(
        (p) => p["instrument-type"] === "Equity Option",
      );

      // Try to detect strategies in order of complexity/likelihood
      // 1. Check for Iron Condor/Butterfly (4 legs)
      if (options.length >= 4) {
        const ironCondors = this.detectIronCondors(options, usedPositions);
        strategies.push(...ironCondors);

        const ironButterflies = this.detectIronButterflies(
          options,
          usedPositions,
        );
        strategies.push(...ironButterflies);
      }

      // 2. Check for 3-leg strategies (Butterflies)
      if (options.length >= 3) {
        const callButterflies = this.detectCallButterflies(
          options,
          usedPositions,
        );
        strategies.push(...callButterflies);
        const putButterflies = this.detectPutButterflies(
          options,
          usedPositions,
        );
        strategies.push(...putButterflies);
        // Removed ratio spread detection - it was incorrectly grouping unrelated positions
      }

      // 3. Check for Covered Calls / Cash Secured Puts
      if (stocks.length > 0 && options.length > 0) {
        const coveredCalls = this.detectCoveredCalls(
          stocks,
          options,
          usedPositions,
        );
        strategies.push(...coveredCalls);

        const collars = this.detectCollars(stocks, options, usedPositions);
        strategies.push(...collars);
      }

      // 4. Check for Vertical Spreads (2 legs)
      if (options.length >= 2) {
        const verticalSpreads = this.detectVerticalSpreads(
          options,
          usedPositions,
        );
        strategies.push(...verticalSpreads);

        const calendarSpreads = this.detectCalendarSpreads(
          options,
          usedPositions,
        );
        strategies.push(...calendarSpreads);

        const straddles = this.detectStraddles(options, usedPositions);
        strategies.push(...straddles);

        const strangles = this.detectStrangles(options, usedPositions);
        strategies.push(...strangles);
      }

      // 5. Check for standalone Cash Secured Puts
      const cashSecuredPuts = this.detectCashSecuredPuts(
        options,
        usedPositions,
      );
      strategies.push(...cashSecuredPuts);
    }

    return strategies;
  }

  /**
   * Create strategy from TastyTrade complex order
   */
  private createStrategyFromComplexOrder(
    complexOrderId: string,
    positions: EnhancedPosition[],
  ): DetectedStrategy {
    const underlyingSymbol = positions[0]["underlying-symbol"];
    const strategyType = this.identifyStrategyTypeFromPositions(positions);
    const strategyName = this.generateStrategyName(strategyType, positions);
    const legs = this.createStrategyLegs(positions);
    const metadata = this.buildStrategyMetadata({
      strategyId: `complex_${complexOrderId}`,
      strategyName,
      strategyType,
      underlyingSymbol,
      detectionSource: "complex_order",
      legs,
      confidence: 1,
      complexOrderId,
      orderDescription: positions[0]["order-description"],
      sourceStrategyId: complexOrderId,
      sourceStrategyName: strategyName,
    });

    return {
      id: `complex_${complexOrderId}`,
      type: strategyType,
      name: strategyName,
      underlyingSymbol,
      legs,
      netPremium: this.calculateNetPremium(positions),
      netDelta: this.calculateNetDelta(positions),
      expirationDate: this.getEarliestExpiration(positions),
      daysToExpiration: this.calculateDaysToExpiration(positions),
      confidence: 1.0, // 100% confidence for complex orders
      metadata,
    };
  }

  /**
   * Apply user overrides to detected strategies
   */
  private applyUserOverrides(
    strategies: DetectedStrategy[],
    allPositions: EnhancedPosition[],
  ): DetectedStrategy[] {
    if (!allPositions.length) return strategies;

    const accountNumber = allPositions[0]["account-number"] || "default";
    const accountOverrides = this.userOverrides[accountNumber];

    if (!accountOverrides) return strategies;

    const modifiedStrategies: DetectedStrategy[] = [];
    const positionMap = new Map(allPositions.map((p) => [p.symbol, p]));

    for (const strategy of strategies) {
      const override = accountOverrides[strategy.id];

      if (override?.isDeleted) {
        // User deleted this strategy - skip it
        continue;
      }

      const modifiedStrategy = { ...strategy };

      if (override) {
        // Apply custom name
        if (override.customName) {
          if (!modifiedStrategy.metadata.originalName) {
            modifiedStrategy.metadata.originalName = modifiedStrategy.name;
          }
          modifiedStrategy.name = override.customName;
          modifiedStrategy.metadata.isUserModified = true;
        }

        // Remove excluded positions
        if (override.excludedPositions.length > 0) {
          modifiedStrategy.legs = modifiedStrategy.legs.filter(
            (leg) => !override.excludedPositions.includes(leg.position.symbol),
          );
          modifiedStrategy.metadata.isUserModified = true;
        }

        // Add included positions
        if (override.addedPositions.length > 0) {
          for (const positionSymbol of override.addedPositions) {
            const position = positionMap.get(positionSymbol);
            if (
              position &&
              !modifiedStrategy.legs.some(
                (leg) => leg.position.symbol === positionSymbol,
              )
            ) {
              const newLeg = this.createStrategyLegs([position])[0];
              modifiedStrategy.legs.push(newLeg);
              modifiedStrategy.metadata.isUserModified = true;
            }
          }
        }

        // Recalculate metrics if modified
        if (modifiedStrategy.metadata.isUserModified) {
          const positions = modifiedStrategy.legs.map((leg) => leg.position);
          modifiedStrategy.netPremium = this.calculateNetPremium(positions);
          modifiedStrategy.netDelta = this.calculateNetDelta(positions);
          modifiedStrategy.daysToExpiration =
            this.calculateDaysToExpiration(positions);
          modifiedStrategy.expirationDate =
            this.getEarliestExpiration(positions);
          modifiedStrategy.metadata.updatedAt = new Date();
        }
      }

      modifiedStrategies.push(modifiedStrategy);
    }

    return modifiedStrategies;
  }

  /**
   * Load user overrides from localStorage
   */
  private loadUserOverrides(): void {
    if (typeof window === "undefined") return; // Server-side safety

    try {
      const stored = localStorage.getItem("strategy-overrides");
      if (stored) {
        this.userOverrides = JSON.parse(stored);
      }
    } catch (error) {
      console.warn("Failed to load strategy overrides:", error);
      this.userOverrides = {};
    }
  }

  /**
   * Save user overrides to localStorage
   */
  private saveUserOverrides(): void {
    if (typeof window === "undefined") return; // Server-side safety

    try {
      localStorage.setItem(
        "strategy-overrides",
        JSON.stringify(this.userOverrides),
      );
    } catch (error) {
      console.warn("Failed to save strategy overrides:", error);
    }
  }

  // ========== StrategyEditActions Implementation ==========

  renameStrategy(strategyId: string, newName: string): void {
    const strategy = this.strategies.find((s) => s.id === strategyId);
    if (!strategy) return;

    // Update strategy
    if (!strategy.metadata.originalName) {
      strategy.metadata.originalName = strategy.name;
    }
    strategy.name = newName;
    strategy.metadata.isUserModified = true;
    strategy.metadata.updatedAt = new Date();

    // Store override
    const accountNumber =
      strategy.legs[0]?.position["account-number"] || "default";
    if (!this.userOverrides[accountNumber]) {
      this.userOverrides[accountNumber] = {};
    }

    this.userOverrides[accountNumber][strategyId] = {
      customName: newName,
      excludedPositions: [],
      addedPositions: [],
      updatedAt: new Date(),
    };

    this.saveUserOverrides();
  }

  removeFromStrategy(strategyId: string, positionSymbol: string): void {
    const strategy = this.strategies.find((s) => s.id === strategyId);
    if (!strategy) return;

    // Remove leg from strategy
    strategy.legs = strategy.legs.filter(
      (leg) => leg.position.symbol !== positionSymbol,
    );
    strategy.metadata.isUserModified = true;
    strategy.metadata.updatedAt = new Date();

    // Store override
    const accountNumber =
      strategy.legs[0]?.position["account-number"] || "default";
    if (!this.userOverrides[accountNumber]) {
      this.userOverrides[accountNumber] = {};
    }

    if (!this.userOverrides[accountNumber][strategyId]) {
      this.userOverrides[accountNumber][strategyId] = {
        excludedPositions: [],
        addedPositions: [],
        updatedAt: new Date(),
      };
    }

    this.userOverrides[accountNumber][strategyId].excludedPositions.push(
      positionSymbol,
    );
    this.userOverrides[accountNumber][strategyId].updatedAt = new Date();

    this.saveUserOverrides();
  }

  createCustomStrategy(
    positionSymbols: string[],
    name?: string,
  ): DetectedStrategy {
    if (positionSymbols.length === 0) {
      throw new Error("Cannot create strategy with no positions");
    }

    // Find the positions (this assumes we have access to current enhanced positions)
    // In a real implementation, this would need to be passed in or retrieved from context
    const positions = this.strategies
      .flatMap((s) => s.legs.map((l) => l.position))
      .filter((p) => positionSymbols.includes(p.symbol));

    if (positions.length === 0) {
      throw new Error("Could not find positions for custom strategy");
    }

    const strategyId = `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const underlyingSymbol = positions[0]["underlying-symbol"];
    const strategyName = name || `${underlyingSymbol} Custom Strategy`;
    const legs = this.createStrategyLegs(positions);
    const metadata = this.buildStrategyMetadata({
      strategyId,
      strategyName,
      strategyType: StrategyType.CUSTOM,
      underlyingSymbol,
      detectionSource: "user_defined",
      legs,
      confidence: 1,
      isUserModified: false,
    });

    const strategy: DetectedStrategy = {
      id: strategyId,
      type: StrategyType.CUSTOM,
      name: strategyName,
      underlyingSymbol,
      legs,
      netPremium: this.calculateNetPremium(positions),
      netDelta: this.calculateNetDelta(positions),
      expirationDate: this.getEarliestExpiration(positions),
      daysToExpiration: this.calculateDaysToExpiration(positions),
      confidence: 1.0, // 100% confidence for user-created strategies
      metadata,
    };

    // Add to strategies array
    this.strategies.push(strategy);

    // Store override to persist the custom strategy
    const accountNumber = positions[0]["account-number"] || "default";
    if (!this.userOverrides[accountNumber]) {
      this.userOverrides[accountNumber] = {};
    }

    this.userOverrides[accountNumber][strategyId] = {
      customName: strategyName,
      excludedPositions: [],
      addedPositions: positionSymbols,
      updatedAt: new Date(),
    };

    this.saveUserOverrides();
    return strategy;
  }

  addToStrategy(strategyId: string, positionSymbol: string): void {
    const strategy = this.strategies.find((s) => s.id === strategyId);
    if (!strategy) {
      throw new Error(`Strategy ${strategyId} not found`);
    }

    // Check if position is already in strategy
    if (strategy.legs.some((leg) => leg.position.symbol === positionSymbol)) {
      return; // Already in strategy
    }

    // Find the position (this would need to be passed in or retrieved from context in a real implementation)
    const allPositions = this.strategies.flatMap((s) =>
      s.legs.map((l) => l.position),
    );
    const position = allPositions.find((p) => p.symbol === positionSymbol);

    if (!position) {
      throw new Error(`Position ${positionSymbol} not found`);
    }

    // Add the leg to strategy
    const newLeg = this.createStrategyLegs([position])[0];
    strategy.legs.push(newLeg);
    strategy.metadata.isUserModified = true;
    strategy.metadata.updatedAt = new Date();

    // Recalculate metrics
    const positions = strategy.legs.map((leg) => leg.position);
    strategy.netPremium = this.calculateNetPremium(positions);
    strategy.netDelta = this.calculateNetDelta(positions);
    strategy.daysToExpiration = this.calculateDaysToExpiration(positions);
    strategy.expirationDate = this.getEarliestExpiration(positions);

    // Store override
    const accountNumber =
      strategy.legs[0]?.position["account-number"] || "default";
    if (!this.userOverrides[accountNumber]) {
      this.userOverrides[accountNumber] = {};
    }

    if (!this.userOverrides[accountNumber][strategyId]) {
      this.userOverrides[accountNumber][strategyId] = {
        excludedPositions: [],
        addedPositions: [],
        updatedAt: new Date(),
      };
    }

    this.userOverrides[accountNumber][strategyId].addedPositions.push(
      positionSymbol,
    );
    this.userOverrides[accountNumber][strategyId].updatedAt = new Date();

    this.saveUserOverrides();
  }

  deleteStrategy(strategyId: string): void {
    // Remove from strategies array
    this.strategies = this.strategies.filter((s) => s.id !== strategyId);
    this.saveUserOverrides();
  }

  resetToAutomatic(strategyId: string): void {
    const strategy = this.strategies.find((s) => s.id === strategyId);
    if (!strategy) return;

    // Reset to original values
    if (strategy.metadata.originalName) {
      strategy.name = strategy.metadata.originalName;
      delete strategy.metadata.originalName;
    }
    strategy.metadata.isUserModified = false;
    strategy.metadata.updatedAt = new Date();

    // Remove from overrides
    const accountNumber =
      strategy.legs[0]?.position["account-number"] || "default";
    if (this.userOverrides[accountNumber]?.[strategyId]) {
      delete this.userOverrides[accountNumber][strategyId];
      this.saveUserOverrides();
    }
  }

  /**
   * Group positions by underlying symbol
   */
  private groupByUnderlying(
    positions: EnhancedPosition[],
  ): Map<string, EnhancedPosition[]> {
    const grouped = new Map<string, EnhancedPosition[]>();

    for (const position of positions) {
      const underlying = position["underlying-symbol"];
      if (!grouped.has(underlying)) {
        grouped.set(underlying, []);
      }
      grouped.get(underlying)!.push(position);
    }

    return grouped;
  }

  /**
   * Detect Iron Condor strategies (4 legs: short call spread + short put spread)
   */
  private detectIronCondors(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];


    // Group by expiration date
    const byExpiration = this.groupByExpiration(options);

    for (const [, expiryOptions] of byExpiration) {
      const calls = expiryOptions.filter((opt) => {
        const parsed = parseOptionSymbol(opt.symbol);
        return parsed?.optionType === "C" && !usedPositions.has(opt.symbol);
      });

      const puts = expiryOptions.filter((opt) => {
        const parsed = parseOptionSymbol(opt.symbol);
        return parsed?.optionType === "P" && !usedPositions.has(opt.symbol);
      });

      // Need at least 2 calls and 2 puts
      if (calls.length < 2 || puts.length < 2) continue;

      // Look for pattern: sell put, buy put (lower strikes), sell call, buy call (higher strikes)
      // All with same expiration and balanced quantities

      for (let i = 0; i < puts.length - 1; i++) {
        for (let j = i + 1; j < puts.length; j++) {
          const put1 = puts[i];
          const put2 = puts[j];

          // Skip if already used
          if (usedPositions.has(put1.symbol) || usedPositions.has(put2.symbol))
            continue;

          // One should be long, one short
          if (put1.quantity * put2.quantity >= 0) continue;

          // Same contract count
          if (Math.abs(put1.quantity) !== Math.abs(put2.quantity)) continue;

          const putLower =
            this.getStrike(put1) < this.getStrike(put2) ? put1 : put2;
          const putHigher =
            this.getStrike(put1) < this.getStrike(put2) ? put2 : put1;

          // Lower put should be long, higher put should be short
          if (putLower.quantity < 0 || putHigher.quantity > 0) continue;

          // Now look for matching call spread
          for (let k = 0; k < calls.length - 1; k++) {
            for (let l = k + 1; l < calls.length; l++) {
              const call1 = calls[k];
              const call2 = calls[l];

              // Skip if already used
              if (
                usedPositions.has(call1.symbol) ||
                usedPositions.has(call2.symbol)
              )
                continue;

              // One should be long, one short
              if (call1.quantity * call2.quantity >= 0) continue;

              // Same contract count as puts
              if (Math.abs(call1.quantity) !== Math.abs(put1.quantity))
                continue;

              const callLower =
                this.getStrike(call1) < this.getStrike(call2) ? call1 : call2;
              const callHigher =
                this.getStrike(call1) < this.getStrike(call2) ? call2 : call1;


              // For Iron Condor: Lower call should be short, higher call should be long
              // This creates a bear call spread (credit spread)
              if (callLower.quantity > 0 || callHigher.quantity < 0) {
                continue;
              }

              // Call strikes should be higher than put strikes
              if (this.getStrike(callLower) <= this.getStrike(putHigher))
                continue;

              // Found an iron condor!
              const id = `ic_${put1.symbol}_${put2.symbol}_${call1.symbol}_${call2.symbol}`;
              const positionsForMetrics = [
                putLower,
                putHigher,
                callLower,
                callHigher,
              ];
              const legs: StrategyLeg[] = [
                {
                  position: putLower,
                  role: LegRole.LONG_PUT,
                  strike: this.getStrike(putLower),
                  optionType: "P",
                  quantity: putLower.quantity,
                },
                {
                  position: putHigher,
                  role: LegRole.SHORT_PUT,
                  strike: this.getStrike(putHigher),
                  optionType: "P",
                  quantity: putHigher.quantity,
                },
                {
                  position: callLower,
                  role: LegRole.SHORT_CALL,
                  strike: this.getStrike(callLower),
                  optionType: "C",
                  quantity: callLower.quantity,
                },
                {
                  position: callHigher,
                  role: LegRole.LONG_CALL,
                  strike: this.getStrike(callHigher),
                  optionType: "C",
                  quantity: callHigher.quantity,
                },
              ];
              const name = `${put1["underlying-symbol"]} ${this.getStrike(putLower)}/${this.getStrike(putHigher)}/${this.getStrike(callLower)}/${this.getStrike(callHigher)} Iron Condor`;

              const strategy = this.createDetectedStrategy({
                id,
                type: StrategyType.IRON_CONDOR,
                name,
                underlyingSymbol: put1["underlying-symbol"],
                legs,
                positions: positionsForMetrics,
                detectionSource: "auto_detected",
                confidence: 0.95, // High confidence for 4-leg pattern
                maxProfit: this.calculateIronCondorMaxProfit(positionsForMetrics),
                maxLoss: this.calculateIronCondorMaxLoss(positionsForMetrics),
                breakevens: this.calculateIronCondorBreakevens(positionsForMetrics),
              });

              strategies.push(strategy);

              // Mark positions as used
              usedPositions.add(put1.symbol);
              usedPositions.add(put2.symbol);
              usedPositions.add(call1.symbol);
              usedPositions.add(call2.symbol);
            }
          }
        }
      }
    }

    return strategies;
  }

  /**
   * Detect Iron Butterfly strategies (4 legs with same middle strike)
   */
  private detectIronButterflies(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    // Group by expiration date
    const byExpiration = this.groupByExpiration(options);

    for (const [, expiryOptions] of byExpiration) {
      // Group by strike
      const byStrike = new Map<
        number,
        { calls: EnhancedPosition[]; puts: EnhancedPosition[] }
      >();

      for (const option of expiryOptions) {
        if (usedPositions.has(option.symbol)) continue;

        const parsed = parseOptionSymbol(option.symbol);
        if (!parsed) continue;

        const strike = this.getStrike(option);
        if (!byStrike.has(strike)) {
          byStrike.set(strike, { calls: [], puts: [] });
        }

        if (parsed.optionType === "C") {
          byStrike.get(strike)!.calls.push(option);
        } else {
          byStrike.get(strike)!.puts.push(option);
        }
      }

      // Look for butterfly pattern:
      // - Middle strike: short call + short put (same strike)
      // - Lower strike: long put
      // - Higher strike: long call
      const strikes = Array.from(byStrike.keys()).sort((a, b) => a - b);

      for (let i = 1; i < strikes.length - 1; i++) {
        const lowerStrike = strikes[i - 1];
        const middleStrike = strikes[i];
        const upperStrike = strikes[i + 1];

        const middle = byStrike.get(middleStrike)!;
        const lower = byStrike.get(lowerStrike)!;
        const upper = byStrike.get(upperStrike)!;

        // Check middle strike has both short call and short put
        const shortCall = middle.calls.find(
          (c) => c.quantity < 0 && !usedPositions.has(c.symbol),
        );
        const shortPut = middle.puts.find(
          (p) => p.quantity < 0 && !usedPositions.has(p.symbol),
        );

        if (!shortCall || !shortPut) continue;
        if (Math.abs(shortCall.quantity) !== Math.abs(shortPut.quantity))
          continue;

        // Check for long put at lower strike
        const longPut = lower.puts.find(
          (p) =>
            p.quantity > 0 &&
            Math.abs(p.quantity) === Math.abs(shortPut.quantity) &&
            !usedPositions.has(p.symbol),
        );

        if (!longPut) continue;

        // Check for long call at upper strike
        const longCall = upper.calls.find(
          (c) =>
            c.quantity > 0 &&
            Math.abs(c.quantity) === Math.abs(shortCall.quantity) &&
            !usedPositions.has(c.symbol),
        );

        if (!longCall) continue;

        // Found an iron butterfly!
        const id = `ib_${longPut.symbol}_${shortPut.symbol}_${shortCall.symbol}_${longCall.symbol}`;
        const positionsForMetrics = [longPut, shortPut, shortCall, longCall];
        const legs: StrategyLeg[] = [
          {
            position: longPut,
            role: LegRole.LONG_PUT,
            strike: lowerStrike,
            optionType: "P",
            quantity: longPut.quantity,
          },
          {
            position: shortPut,
            role: LegRole.SHORT_PUT,
            strike: middleStrike,
            optionType: "P",
            quantity: shortPut.quantity,
          },
          {
            position: shortCall,
            role: LegRole.SHORT_CALL,
            strike: middleStrike,
            optionType: "C",
            quantity: shortCall.quantity,
          },
          {
            position: longCall,
            role: LegRole.LONG_CALL,
            strike: upperStrike,
            optionType: "C",
            quantity: longCall.quantity,
          },
        ];
        const name = `${longPut["underlying-symbol"]} ${lowerStrike}/${middleStrike}/${upperStrike} Iron Butterfly`;

        const strategy = this.createDetectedStrategy({
          id,
          type: StrategyType.IRON_BUTTERFLY,
          name,
          underlyingSymbol: longPut["underlying-symbol"],
          legs,
          positions: positionsForMetrics,
          detectionSource: "auto_detected",
          confidence: 0.95,
          maxProfit: this.calculateButterflyMaxProfit(positionsForMetrics),
          maxLoss: this.calculateButterflyMaxLoss(positionsForMetrics),
        });

        strategies.push(strategy);

        // Mark positions as used
        usedPositions.add(longPut.symbol);
        usedPositions.add(shortPut.symbol);
        usedPositions.add(shortCall.symbol);
        usedPositions.add(longCall.symbol);
      }
    }

    return strategies;
  }

  /**
   * Detect Covered Call strategies (long stock + short call)
   */
  private detectCoveredCalls(
    stocks: EnhancedPosition[],
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    for (const stock of stocks) {
      if (stock.quantity <= 0 || usedPositions.has(stock.symbol)) continue;

      // Look for short calls
      const shortCalls = options.filter((opt) => {
        if (usedPositions.has(opt.symbol)) return false;

        const parsed = parseOptionSymbol(opt.symbol);
        return (
          parsed?.optionType === "C" &&
          opt.quantity < 0 &&
          opt["underlying-symbol"] === stock.symbol
        );
      });

      for (const call of shortCalls) {
        // Check if quantities match (100 shares per contract)
        const contractsNeeded = Math.floor(stock.quantity / 100);
        const contractsSold = Math.abs(call.quantity);

        if (contractsSold <= contractsNeeded) {
          const legs: StrategyLeg[] = [
            {
              position: stock,
              role: LegRole.LONG_STOCK,
              strike: 0,
              optionType: "C",
              quantity: contractsSold * 100,
            },
            {
              position: call,
              role: LegRole.SHORT_CALL,
              strike: this.getStrike(call),
              optionType: "C",
              quantity: call.quantity,
            },
          ];
          const metadata = this.buildStrategyMetadata({
            strategyId: `cc_${stock.symbol}_${call.symbol}`,
            strategyName: `${stock.symbol} ${this.getStrike(call)} Covered Call`,
            strategyType: StrategyType.COVERED_CALL,
            underlyingSymbol: stock.symbol,
            detectionSource: "auto_detected",
            legs,
            confidence: 0.9,
          });

          const strategy: DetectedStrategy = {
            id: metadata.strategyId,
            type: StrategyType.COVERED_CALL,
            name: metadata.strategyName,
            underlyingSymbol: stock.symbol,
            legs,
            netPremium: this.calculateNetPremium([call]),
            netDelta:
              (stock.positionDelta || 0) *
                ((contractsSold * 100) / stock.quantity) +
              (call.positionDelta || 0),
            expirationDate: parseOptionSymbol(call.symbol)?.expiry || "",
            daysToExpiration: call.dte || 0,
            confidence: 0.9,
            maxProfit: this.calculateCoveredCallMaxProfit(
              stock,
              call,
              contractsSold,
            ),
            metadata,
          };

          strategies.push(strategy);

          // Mark positions as used (partial stock position if needed)
          if (contractsSold * 100 === stock.quantity) {
            usedPositions.add(stock.symbol);
          }
          usedPositions.add(call.symbol);
        }
      }
    }

    return strategies;
  }

  /**
   * Detect Collar strategies (long stock + long put + short call)
   */
  private detectCollars(
    stocks: EnhancedPosition[],
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    for (const stock of stocks) {
      if (stock.quantity <= 0 || usedPositions.has(stock.symbol)) continue;

      // Group options by expiration
      const optionsByExpiry = this.groupByExpiration(
        options.filter(
          (opt) =>
            opt["underlying-symbol"] === stock.symbol &&
            !usedPositions.has(opt.symbol),
        ),
      );

      for (const [expiry, expiryOptions] of optionsByExpiry) {
        // Look for long put and short call with same expiration
        const longPuts = expiryOptions.filter((opt) => {
          const parsed = parseOptionSymbol(opt.symbol);
          return parsed?.optionType === "P" && opt.quantity > 0;
        });

        const shortCalls = expiryOptions.filter((opt) => {
          const parsed = parseOptionSymbol(opt.symbol);
          return parsed?.optionType === "C" && opt.quantity < 0;
        });

        for (const put of longPuts) {
          for (const call of shortCalls) {
            // Check if quantities match
            if (Math.abs(put.quantity) !== Math.abs(call.quantity)) continue;

            const contractCount = Math.abs(put.quantity);
            const sharesNeeded = contractCount * 100;

            if (sharesNeeded <= stock.quantity) {
              const legs: StrategyLeg[] = [
                {
                  position: stock,
                  role: LegRole.LONG_STOCK,
                  strike: 0,
                  optionType: "C",
                  quantity: sharesNeeded,
                },
                {
                  position: put,
                  role: LegRole.PROTECTIVE_PUT,
                  strike: this.getStrike(put),
                  optionType: "P",
                  quantity: put.quantity,
                },
                {
                  position: call,
                  role: LegRole.SHORT_CALL,
                  strike: this.getStrike(call),
                  optionType: "C",
                  quantity: call.quantity,
                },
              ];
              const metadata = this.buildStrategyMetadata({
                strategyId: `collar_${stock.symbol}_${put.symbol}_${call.symbol}`,
                strategyName: `${stock.symbol} Collar`,
                strategyType: StrategyType.COLLAR,
                underlyingSymbol: stock.symbol,
                detectionSource: "auto_detected",
                legs,
                confidence: 0.85,
              });

              const strategy: DetectedStrategy = {
                id: metadata.strategyId,
                type: StrategyType.COLLAR,
                name: metadata.strategyName,
                underlyingSymbol: stock.symbol,
                legs,
                netPremium: this.calculateNetPremium([put, call]),
                netDelta:
                  (stock.positionDelta || 0) * (sharesNeeded / stock.quantity) +
                  (put.positionDelta || 0) +
                  (call.positionDelta || 0),
                expirationDate: expiry,
                daysToExpiration: put.dte || 0,
                confidence: 0.85,
                metadata,
              };

              strategies.push(strategy);

              // Mark positions as used
              if (sharesNeeded === stock.quantity) {
                usedPositions.add(stock.symbol);
              }
              usedPositions.add(put.symbol);
              usedPositions.add(call.symbol);
            }
          }
        }
      }
    }

    return strategies;
  }

  /**
   * Detect Vertical Spread strategies (same expiration, different strikes)
   */
  private detectVerticalSpreads(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    // Group by expiration and option type
    const byExpirationAndType = new Map<string, EnhancedPosition[]>();

    for (const option of options) {
      if (usedPositions.has(option.symbol)) continue;

      const parsed = parseOptionSymbol(option.symbol);
      if (!parsed) continue;

      const key = `${parsed.expiry}_${parsed.optionType}`;
      if (!byExpirationAndType.has(key)) {
        byExpirationAndType.set(key, []);
      }
      byExpirationAndType.get(key)!.push(option);
    }

    for (const sameTypeOptions of byExpirationAndType.values()) {
      if (sameTypeOptions.length < 2) continue;

      // Look for pairs with opposite quantities (one long, one short)
      for (let i = 0; i < sameTypeOptions.length - 1; i++) {
        for (let j = i + 1; j < sameTypeOptions.length; j++) {
          const opt1 = sameTypeOptions[i];
          const opt2 = sameTypeOptions[j];

          // Skip if either position is already used
          if (
            usedPositions.has(opt1.symbol) ||
            usedPositions.has(opt2.symbol)
          ) {
            continue;
          }

          // One should be long, one short
          if (opt1.quantity * opt2.quantity >= 0) continue;

          // Same contract count
          if (Math.abs(opt1.quantity) !== Math.abs(opt2.quantity)) continue;

          const lower =
            this.getStrike(opt1) < this.getStrike(opt2) ? opt1 : opt2;
          const higher =
            this.getStrike(opt1) < this.getStrike(opt2) ? opt2 : opt1;
          const parsed = parseOptionSymbol(lower.symbol)!;

          // Determine spread type
          let spreadName = "";
          if (parsed.optionType === "C") {
            if (lower.quantity > 0) {
              spreadName = "Bull Call Spread";
            } else {
              spreadName = "Bear Call Spread";
            }
          } else {
            if (lower.quantity < 0) {
              spreadName = "Bull Put Spread";
            } else {
              spreadName = "Bear Put Spread";
            }
          }

          const legs: StrategyLeg[] = [
            {
              position: lower,
              role:
                lower.quantity > 0
                  ? parsed.optionType === "C"
                    ? LegRole.LONG_CALL
                    : LegRole.LONG_PUT
                  : parsed.optionType === "C"
                    ? LegRole.SHORT_CALL
                    : LegRole.SHORT_PUT,
              strike: this.getStrike(lower),
              optionType: parsed.optionType,
              quantity: lower.quantity,
            },
            {
              position: higher,
              role:
                higher.quantity > 0
                  ? parsed.optionType === "C"
                    ? LegRole.LONG_CALL
                    : LegRole.LONG_PUT
                  : parsed.optionType === "C"
                    ? LegRole.SHORT_CALL
                    : LegRole.SHORT_PUT,
              strike: this.getStrike(higher),
              optionType: parsed.optionType,
              quantity: higher.quantity,
            },
          ];
          const strategyName = `${opt1["underlying-symbol"]} ${this.getStrike(lower)}/${this.getStrike(higher)} ${spreadName}`;
          const metadata = this.buildStrategyMetadata({
            strategyId: `vs_${opt1.symbol}_${opt2.symbol}`,
            strategyName,
            strategyType: StrategyType.VERTICAL_SPREAD,
            underlyingSymbol: opt1["underlying-symbol"],
            detectionSource: "auto_detected",
            legs,
            confidence: 0.9,
          });

          const strategy: DetectedStrategy = {
            id: metadata.strategyId,
            type: StrategyType.VERTICAL_SPREAD,
            name: strategyName,
            underlyingSymbol: opt1["underlying-symbol"],
            legs,
            netPremium: this.calculateNetPremium([lower, higher]),
            netDelta: (lower.positionDelta || 0) + (higher.positionDelta || 0),
            expirationDate: parsed.expiry,
            daysToExpiration: lower.dte || 0,
            confidence: 0.9,
            metadata,
          };

          strategies.push(strategy);

          // Mark positions as used
          usedPositions.add(opt1.symbol);
          usedPositions.add(opt2.symbol);
        }
      }
    }

    return strategies;
  }

  /**
   * Detect Calendar Spread strategies (same strike, different expirations)
   */
  private detectCalendarSpreads(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    // Group by strike and option type
    const byStrikeAndType = new Map<string, EnhancedPosition[]>();

    for (const option of options) {
      if (usedPositions.has(option.symbol)) continue;

      const parsed = parseOptionSymbol(option.symbol);
      if (!parsed) continue;

      const strike = this.getStrike(option);
      const key = `${strike}_${parsed.optionType}`;

      if (!byStrikeAndType.has(key)) {
        byStrikeAndType.set(key, []);
      }
      byStrikeAndType.get(key)!.push(option);
    }

    for (const sameStrikeOptions of byStrikeAndType.values()) {
      if (sameStrikeOptions.length < 2) continue;

      // Sort by expiration
      sameStrikeOptions.sort((a, b) => (a.dte || 0) - (b.dte || 0));

      // Look for pairs with opposite quantities
      for (let i = 0; i < sameStrikeOptions.length - 1; i++) {
        for (let j = i + 1; j < sameStrikeOptions.length; j++) {
          const nearTerm = sameStrikeOptions[i];
          const farTerm = sameStrikeOptions[j];

          // One should be long, one short
          if (nearTerm.quantity * farTerm.quantity >= 0) continue;

          // Same contract count
          if (Math.abs(nearTerm.quantity) !== Math.abs(farTerm.quantity))
            continue;

          // Typically sell near-term, buy far-term
          if (nearTerm.quantity > 0) continue;

          const parsed = parseOptionSymbol(nearTerm.symbol)!;

          const legs: StrategyLeg[] = [
            {
              position: nearTerm,
              role:
                parsed.optionType === "C"
                  ? LegRole.SHORT_CALL
                  : LegRole.SHORT_PUT,
              strike: this.getStrike(nearTerm),
              optionType: parsed.optionType,
              quantity: nearTerm.quantity,
            },
            {
              position: farTerm,
              role:
                parsed.optionType === "C"
                  ? LegRole.LONG_CALL
                  : LegRole.LONG_PUT,
              strike: this.getStrike(farTerm),
              optionType: parsed.optionType,
              quantity: farTerm.quantity,
            },
          ];
          const strategyName = `${nearTerm["underlying-symbol"]} ${this.getStrike(nearTerm)} Calendar ${parsed.optionType === "C" ? "Call" : "Put"} Spread`;
          const metadata = this.buildStrategyMetadata({
            strategyId: `cal_${nearTerm.symbol}_${farTerm.symbol}`,
            strategyName,
            strategyType: StrategyType.CALENDAR_SPREAD,
            underlyingSymbol: nearTerm["underlying-symbol"],
            detectionSource: "auto_detected",
            legs,
            confidence: 0.85,
          });

          const strategy: DetectedStrategy = {
            id: metadata.strategyId,
            type: StrategyType.CALENDAR_SPREAD,
            name: strategyName,
            underlyingSymbol: nearTerm["underlying-symbol"],
            legs,
            netPremium: this.calculateNetPremium([nearTerm, farTerm]),
            netDelta:
              (nearTerm.positionDelta || 0) + (farTerm.positionDelta || 0),
            expirationDate: parseOptionSymbol(nearTerm.symbol)?.expiry || "",
            daysToExpiration: nearTerm.dte || 0,
            confidence: 0.85,
            metadata,
          };

          strategies.push(strategy);

          // Mark positions as used
          usedPositions.add(nearTerm.symbol);
          usedPositions.add(farTerm.symbol);
        }
      }
    }

    return strategies;
  }

  /**
   * Detect Straddle strategies (same strike call and put)
   */
  private detectStraddles(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    // Group by expiration and strike
    const byExpirationAndStrike = new Map<
      string,
      { calls: EnhancedPosition[]; puts: EnhancedPosition[] }
    >();

    for (const option of options) {
      if (usedPositions.has(option.symbol)) continue;

      const parsed = parseOptionSymbol(option.symbol);
      if (!parsed) continue;

      const strike = this.getStrike(option);
      const key = `${parsed.expiry}_${strike}`;

      if (!byExpirationAndStrike.has(key)) {
        byExpirationAndStrike.set(key, { calls: [], puts: [] });
      }

      if (parsed.optionType === "C") {
        byExpirationAndStrike.get(key)!.calls.push(option);
      } else {
        byExpirationAndStrike.get(key)!.puts.push(option);
      }
    }

    for (const { calls, puts } of byExpirationAndStrike.values()) {
      if (calls.length === 0 || puts.length === 0) continue;

      // Look for matching call and put with same quantity and direction
      for (const call of calls) {
        for (const put of puts) {
          // Same direction (both long or both short)
          if (call.quantity > 0 !== put.quantity > 0) continue;

          // Same contract count
          if (Math.abs(call.quantity) !== Math.abs(put.quantity)) continue;

          const isLong = call.quantity > 0;

          const legs: StrategyLeg[] = [
            {
              position: call,
              role: isLong ? LegRole.LONG_CALL : LegRole.SHORT_CALL,
              strike: this.getStrike(call),
              optionType: "C",
              quantity: call.quantity,
            },
            {
              position: put,
              role: isLong ? LegRole.LONG_PUT : LegRole.SHORT_PUT,
              strike: this.getStrike(put),
              optionType: "P",
              quantity: put.quantity,
            },
          ];
          const strategyName = `${call["underlying-symbol"]} ${this.getStrike(call)} ${isLong ? "Long" : "Short"} Straddle`;
          const metadata = this.buildStrategyMetadata({
            strategyId: `straddle_${call.symbol}_${put.symbol}`,
            strategyName,
            strategyType: StrategyType.STRADDLE,
            underlyingSymbol: call["underlying-symbol"],
            detectionSource: "auto_detected",
            legs,
            confidence: 0.95,
          });

          const strategy: DetectedStrategy = {
            id: metadata.strategyId,
            type: StrategyType.STRADDLE,
            name: strategyName,
            underlyingSymbol: call["underlying-symbol"],
            legs,
            netPremium: this.calculateNetPremium([call, put]),
            netDelta: (call.positionDelta || 0) + (put.positionDelta || 0),
            expirationDate: parseOptionSymbol(call.symbol)?.expiry || "",
            daysToExpiration: call.dte || 0,
            confidence: 0.95,
            metadata,
          };

          strategies.push(strategy);

          // Mark positions as used
          usedPositions.add(call.symbol);
          usedPositions.add(put.symbol);
        }
      }
    }

    return strategies;
  }

  /**
   * Detect Strangle strategies (different strikes call and put)
   */
  private detectStrangles(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    // Group by expiration
    const byExpiration = this.groupByExpiration(options);

    for (const [expiration, expiryOptions] of byExpiration) {
      const calls = expiryOptions.filter((opt) => {
        const parsed = parseOptionSymbol(opt.symbol);
        return parsed?.optionType === "C" && !usedPositions.has(opt.symbol);
      });

      const puts = expiryOptions.filter((opt) => {
        const parsed = parseOptionSymbol(opt.symbol);
        return parsed?.optionType === "P" && !usedPositions.has(opt.symbol);
      });

      // Look for call and put with different strikes but same quantity/direction
      for (const call of calls) {
        for (const put of puts) {
          // Same direction
          if (call.quantity > 0 !== put.quantity > 0) continue;

          // Same contract count
          if (Math.abs(call.quantity) !== Math.abs(put.quantity)) continue;

          // Different strikes
          const callStrike = this.getStrike(call);
          const putStrike = this.getStrike(put);
          if (callStrike === putStrike) continue;

          // Call strike should be higher than put strike
          if (callStrike < putStrike) continue;

          const isLong = call.quantity > 0;

          const legs: StrategyLeg[] = [
            {
              position: put,
              role: isLong ? LegRole.LONG_PUT : LegRole.SHORT_PUT,
              strike: putStrike,
              optionType: "P",
              quantity: put.quantity,
            },
            {
              position: call,
              role: isLong ? LegRole.LONG_CALL : LegRole.SHORT_CALL,
              strike: callStrike,
              optionType: "C",
              quantity: call.quantity,
            },
          ];
          const strategyName = `${call["underlying-symbol"]} ${putStrike}/${callStrike} ${isLong ? "Long" : "Short"} Strangle`;
          const metadata = this.buildStrategyMetadata({
            strategyId: `strangle_${call.symbol}_${put.symbol}`,
            strategyName,
            strategyType: StrategyType.STRANGLE,
            underlyingSymbol: call["underlying-symbol"],
            detectionSource: "auto_detected",
            legs,
            confidence: 0.9,
          });

          const strategy: DetectedStrategy = {
            id: metadata.strategyId,
            type: StrategyType.STRANGLE,
            name: strategyName,
            underlyingSymbol: call["underlying-symbol"],
            legs,
            netPremium: this.calculateNetPremium([call, put]),
            netDelta: (call.positionDelta || 0) + (put.positionDelta || 0),
            expirationDate: expiration,
            daysToExpiration: call.dte || 0,
            confidence: 0.9,
            metadata,
          };

          strategies.push(strategy);

          // Mark positions as used
          usedPositions.add(call.symbol);
          usedPositions.add(put.symbol);
        }
      }
    }

    return strategies;
  }

  /**
   * Detect standalone Cash Secured Put strategies
   */
  private detectCashSecuredPuts(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    for (const option of options) {
      if (usedPositions.has(option.symbol)) continue;

      const parsed = parseOptionSymbol(option.symbol);
      if (!parsed || parsed.optionType !== "P" || option.quantity >= 0)
        continue;

      // Short put not part of another strategy
      const legs: StrategyLeg[] = [
        {
          position: option,
          role: LegRole.SHORT_PUT,
          strike: this.getStrike(option),
          optionType: "P",
          quantity: option.quantity,
        },
      ];
      const strategyName = `${option["underlying-symbol"]} ${this.getStrike(option)} Cash Secured Put`;
      const metadata = this.buildStrategyMetadata({
        strategyId: `csp_${option.symbol}`,
        strategyName,
        strategyType: StrategyType.CASH_SECURED_PUT,
        underlyingSymbol: option["underlying-symbol"],
        detectionSource: "auto_detected",
        legs,
        confidence: 0.8,
      });

      const strategy: DetectedStrategy = {
        id: metadata.strategyId,
        type: StrategyType.CASH_SECURED_PUT,
        name: strategyName,
        underlyingSymbol: option["underlying-symbol"],
        legs,
        netPremium: this.calculateNetPremium([option]),
        netDelta: option.positionDelta || 0,
        expirationDate: parsed.expiry,
        daysToExpiration: option.dte || 0,
        confidence: 0.8, // Lower confidence as it could be part of a spread we missed
        metadata,
      };

      strategies.push(strategy);
      usedPositions.add(option.symbol);
    }

    return strategies;
  }

  /**
   * Helper method to get strike price from option position
   */
  private getStrike(option: EnhancedPosition): number {
    const parsed = parseOptionSymbol(option.symbol);
    if (!parsed) return 0;

    // parseOptionSymbol already converts the strike to the correct dollar value
    return parsed.strike;
  }

  /**
   * Group options by expiration date
   */
  private groupByExpiration(
    options: EnhancedPosition[],
  ): Map<string, EnhancedPosition[]> {
    const grouped = new Map<string, EnhancedPosition[]>();

    for (const option of options) {
      const parsed = parseOptionSymbol(option.symbol);
      if (!parsed) continue;

      const expiry = parsed.expiry;
      if (!grouped.has(expiry)) {
        grouped.set(expiry, []);
      }
      grouped.get(expiry)!.push(option);
    }

    return grouped;
  }

  /**
   * Calculate net premium collected/paid for a strategy
   */
  private calculateNetPremium(positions: EnhancedPosition[]): number {
    let netPremium = 0;

    for (const position of positions) {
      const avgPrice = parseFloat(
        String(position["average-open-price"] ?? 0),
      ) || 0;
      const multiplier =
        position.multiplier ||
        (position["instrument-type"] === "Equity Option" ? 100 : 1);

      // Negative quantity means we sold (collected premium)
      // Positive quantity means we bought (paid premium)
      netPremium -= position.quantity * avgPrice * multiplier;
    }

    return netPremium;
  }

  /**
   * Calculate max profit for Iron Condor
   */
  private calculateIronCondorMaxProfit(legs: EnhancedPosition[]): number {
    // Max profit is the net credit received
    return Math.max(0, this.calculateNetPremium(legs));
  }

  /**
   * Calculate max loss for Iron Condor
   */
  private calculateIronCondorMaxLoss(legs: EnhancedPosition[]): number {
    // Find the spreads
    const puts = legs
      .filter((leg) => {
        const parsed = parseOptionSymbol(leg.symbol);
        return parsed?.optionType === "P";
      })
      .sort((a, b) => this.getStrike(a) - this.getStrike(b));

    const calls = legs
      .filter((leg) => {
        const parsed = parseOptionSymbol(leg.symbol);
        return parsed?.optionType === "C";
      })
      .sort((a, b) => this.getStrike(a) - this.getStrike(b));

    if (puts.length !== 2 || calls.length !== 2) return 0;

    const putSpread = this.getStrike(puts[1]) - this.getStrike(puts[0]);
    const callSpread = this.getStrike(calls[1]) - this.getStrike(calls[0]);
    const maxSpread = Math.max(putSpread, callSpread);

    const contracts = Math.abs(puts[0].quantity);
    const netCredit = this.calculateNetPremium(legs);

    return -(maxSpread * 100 * contracts - netCredit);
  }

  /**
   * Calculate breakeven points for Iron Condor
   */
  private calculateIronCondorBreakevens(legs: EnhancedPosition[]): number[] {
    const puts = legs
      .filter((leg) => {
        const parsed = parseOptionSymbol(leg.symbol);
        return parsed?.optionType === "P";
      })
      .sort((a, b) => this.getStrike(a) - this.getStrike(b));

    const calls = legs
      .filter((leg) => {
        const parsed = parseOptionSymbol(leg.symbol);
        return parsed?.optionType === "C";
      })
      .sort((a, b) => this.getStrike(a) - this.getStrike(b));

    if (puts.length !== 2 || calls.length !== 2) return [];

    const netCredit = this.calculateNetPremium(legs);
    const creditPerContract = netCredit / Math.abs(puts[0].quantity) / 100;

    // Lower breakeven: short put strike - credit per contract
    const lowerBreakeven = this.getStrike(puts[1]) - creditPerContract;

    // Upper breakeven: short call strike + credit per contract
    const upperBreakeven = this.getStrike(calls[0]) + creditPerContract;

    return [lowerBreakeven, upperBreakeven];
  }

  /**
   * Calculate max profit for Butterfly
   */
  private calculateButterflyMaxProfit(legs: EnhancedPosition[]): number {
    // Sort legs by strike
    const sortedLegs = [...legs].sort(
      (a, b) => this.getStrike(a) - this.getStrike(b),
    );

    // Middle strikes should be the short options
    const middleStrike = this.getStrike(sortedLegs[1]); // Assuming 4 legs with middle 2 at same strike
    const lowerStrike = this.getStrike(sortedLegs[0]);

    const spreadWidth = middleStrike - lowerStrike;
    const netDebit = Math.abs(this.calculateNetPremium(legs));
    const contracts = Math.abs(sortedLegs[0].quantity);

    return spreadWidth * 100 * contracts - netDebit;
  }

  /**
   * Calculate max loss for Butterfly
   */
  private calculateButterflyMaxLoss(legs: EnhancedPosition[]): number {
    // Max loss is the net debit paid
    return -Math.abs(this.calculateNetPremium(legs));
  }

  /**
   * Calculate max profit for Covered Call
   */
  private calculateCoveredCallMaxProfit(
    stock: EnhancedPosition,
    call: EnhancedPosition,
    contracts: number,
  ): number {
    const strikePrice = this.getStrike(call);
    const stockCostBasis = parseFloat(
      String(stock["average-open-price"] ?? 0),
    ) || 0;
    const callPremium = parseFloat(
      String(call["average-open-price"] ?? 0),
    ) || 0;

    // Max profit = (strike - stock cost) * shares + premium collected
    return (
      (strikePrice - stockCostBasis) * contracts * 100 +
      Math.abs(callPremium * contracts * 100)
    );
  }

  // ========== Helper Methods for Complex Order Processing ==========

  /**
   * Identify strategy type from positions in a complex order
   */
  private identifyStrategyTypeFromPositions(
    positions: EnhancedPosition[],
  ): StrategyKind {
    const stocks = positions.filter((p) => p["instrument-type"] === "Equity");
    const options = positions.filter(
      (p) => p["instrument-type"] === "Equity Option",
    );

    // Analyze by position count and types
    if (positions.length === 1) {
      if (options.length === 1) {
        const parsed = parseOptionSymbol(options[0].symbol);
        return parsed?.optionType === "P" && options[0].quantity < 0
          ? StrategyType.CASH_SECURED_PUT
          : StrategyType.CUSTOM;
      }
      return StrategyType.CUSTOM;
    }

    if (positions.length === 2) {
      if (stocks.length === 1 && options.length === 1) {
        const parsed = parseOptionSymbol(options[0].symbol);
        if (parsed?.optionType === "C" && options[0].quantity < 0) {
          return StrategyType.COVERED_CALL;
        }
      }

      if (options.length === 2) {
        const option1 = options[0];
        const option2 = options[1];
        const parsed1 = parseOptionSymbol(option1.symbol);
        const parsed2 = parseOptionSymbol(option2.symbol);

        if (parsed1 && parsed2) {
          // Same expiration = vertical spread or straddle/strangle
          if (parsed1.expiry === parsed2.expiry) {
            if (parsed1.optionType === parsed2.optionType) {
              return StrategyType.VERTICAL_SPREAD;
            } else {
              // Call + Put
              return this.getStrike(option1) === this.getStrike(option2)
                ? StrategyType.STRADDLE
                : StrategyType.STRANGLE;
            }
          } else {
            // Different expirations = calendar spread
            return StrategyType.CALENDAR_SPREAD;
          }
        }
      }
    }

    if (positions.length === 3) {
      if (stocks.length === 1 && options.length === 2) {
        return StrategyType.COLLAR;
      }
    }

    if (positions.length === 4 && options.length === 4) {
      // Check if it's an iron condor or iron butterfly
      const strikes = options
        .map((opt) => this.getStrike(opt))
        .sort((a, b) => a - b);
      const uniqueStrikes = [...new Set(strikes)];

      if (uniqueStrikes.length === 3) {
        return StrategyType.IRON_BUTTERFLY;
      } else if (uniqueStrikes.length === 4) {
        return StrategyType.IRON_CONDOR;
      }
    }

    return StrategyType.CUSTOM;
  }

  /**
   * Generate a display name for the strategy
   */
  private generateStrategyName(
    type: StrategyKind,
    positions: EnhancedPosition[],
  ): string {
    const [firstPosition] = positions;
    if (!firstPosition) {
      return "Unknown Strategy";
    }

    const underlying = firstPosition["underlying-symbol"] ?? "Unknown";
    const orderDescription = firstPosition["order-description"] ?? "";

    // Use order description if available and meaningful
    if (
      orderDescription &&
      orderDescription.length > 0 &&
      orderDescription !== "Option"
    ) {
      return `${underlying} ${orderDescription}`;
    }

    // Generate name based on strategy type
    switch (type) {
      case StrategyType.COVERED_CALL: {
        const call = positions.find(
          (p) => p["instrument-type"] === "Equity Option",
        );
        const strike = call ? this.getStrike(call) : 0;
        return `${underlying} ${strike} Covered Call`;
      }

      case StrategyType.CASH_SECURED_PUT: {
        const put = positions[0];
        const strike = put ? this.getStrike(put) : 0;
        return `${underlying} ${strike} Cash Secured Put`;
      }

      case StrategyType.VERTICAL_SPREAD: {
        const options = positions.filter(
          (p) => p["instrument-type"] === "Equity Option",
        );
        if (options.length === 2) {
          const strikes = options
            .map((opt) => this.getStrike(opt))
            .sort((a, b) => a - b);
          const parsed = parseOptionSymbol(options[0].symbol);
          const primaryLeg = options.find(
            (opt) => this.getStrike(opt) === strikes[0],
          );
          const direction = (primaryLeg?.quantity ?? 0) > 0 ? "Bull" : "Bear";
          const optType = parsed?.optionType === "C" ? "Call" : "Put";
          return `${underlying} ${strikes[0]}/${strikes[1]} ${direction} ${optType} Spread`;
        }
        break;
      }

      case StrategyType.IRON_CONDOR: {
        const options = positions.filter(
          (p) => p["instrument-type"] === "Equity Option",
        );
        const strikes = options
          .map((opt) => this.getStrike(opt))
          .sort((a, b) => a - b);
        return `${underlying} ${strikes[0]}/${strikes[1]}/${strikes[2]}/${strikes[3]} Iron Condor`;
      }

      case StrategyType.IRON_BUTTERFLY: {
        const options = positions.filter(
          (p) => p["instrument-type"] === "Equity Option",
        );
        const strikes = [
          ...new Set(options.map((opt) => this.getStrike(opt))),
        ].sort((a, b) => a - b);
        return `${underlying} ${strikes[0]}/${strikes[1]}/${strikes[2]} Iron Butterfly`;
      }

      case StrategyType.STRADDLE: {
        const options = positions.filter(
          (p) => p["instrument-type"] === "Equity Option",
        );
        const firstOption = options[0];
        if (!firstOption) {
          return `${underlying} Straddle`;
        }
        const strike = this.getStrike(firstOption);
        const direction = firstOption.quantity > 0 ? "Long" : "Short";
        return `${underlying} ${strike} ${direction} Straddle`;
      }

      case StrategyType.STRANGLE: {
        const options = positions.filter(
          (p) => p["instrument-type"] === "Equity Option",
        );
        if (options.length < 2) {
          return `${underlying} Strangle`;
        }
        const strikes = options
          .map((opt) => this.getStrike(opt))
          .sort((a, b) => a - b);
        const direction = options[0].quantity > 0 ? "Long" : "Short";
        return `${underlying} ${strikes[0]}/${strikes[1]} ${direction} Strangle`;
      }

      case StrategyType.CALENDAR_SPREAD: {
        const options = positions.filter(
          (p) => p["instrument-type"] === "Equity Option",
        );
        const strike = this.getStrike(options[0]);
        const parsed = parseOptionSymbol(options[0].symbol);
        const optType = parsed?.optionType === "C" ? "Call" : "Put";
        return `${underlying} ${strike} Calendar ${optType} Spread`;
      }

      case StrategyType.COLLAR: {
        return `${underlying} Collar`;
      }

      default:
        return `${underlying} Custom Strategy`;
    }

    return `${underlying} Custom Strategy`;
  }

  /**
   * Create strategy legs from positions
   */
  private createStrategyLegs(positions: EnhancedPosition[]): StrategyLeg[] {
    return positions.map((position) => {
      const parsed = parseOptionSymbol(position.symbol);
      let role: LegRoleValue;

      if (position["instrument-type"] === "Equity") {
        role = position.quantity > 0 ? LegRole.LONG_STOCK : LegRole.SHORT_STOCK;
      } else {
        // Option
        const isCall = parsed?.optionType === "C";
        const isLong = position.quantity > 0;

        if (isCall) {
          role = isLong ? LegRole.LONG_CALL : LegRole.SHORT_CALL;
        } else {
          role = isLong ? LegRole.LONG_PUT : LegRole.SHORT_PUT;
        }
      }

      return {
        position,
        role,
        strike: parsed ? parsed.strike : 0,
        optionType: parsed?.optionType || "C",
        quantity: position.quantity,
      };
    });
  }

  /**
   * Calculate net delta for a strategy
   */
  private calculateNetDelta(positions: EnhancedPosition[]): number {
    return positions.reduce((sum, pos) => sum + (pos.positionDelta || 0), 0);
  }

  /**
   * Get the earliest expiration date from positions
   */
  private getEarliestExpiration(positions: EnhancedPosition[]): string {
    const options = positions.filter(
      (p) => p["instrument-type"] === "Equity Option",
    );

    if (options.length === 0) {
      return "";
    }

    // Get all expiration dates and find the earliest
    const expirations = options
      .map((opt) => parseOptionSymbol(opt.symbol)?.expiry)
      .filter((exp): exp is string => exp !== undefined)
      .sort();

    return expirations[0] || "";
  }

  /**
   * Calculate days to expiration for a strategy (minimum across all legs)
   */
  private calculateDaysToExpiration(positions: EnhancedPosition[]): number {
    const options = positions.filter(
      (p) => p["instrument-type"] === "Equity Option",
    );

    if (options.length === 0) {
      return 0;
    }

    const dtes = options
      .map((opt) => opt.dte)
      .filter((dte): dte is number => dte !== null);

    return dtes.length > 0 ? Math.min(...dtes) : 0;
  }

  /**
   * Detect Call Butterfly strategies (3 legs, all calls)
   * Pattern: 1 long call at lower strike, 2 short calls at middle strike, 1 long call at higher strike
   */
  private detectCallButterflies(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    // Group by expiration date
    const byExpiration = this.groupByExpiration(options);

    for (const [expiration, expiryOptions] of byExpiration) {
      // Filter to only calls
      const calls = expiryOptions.filter((opt) => {
        const parsed = parseOptionSymbol(opt.symbol);
        return parsed?.optionType === "C" && !usedPositions.has(opt.symbol);
      });

      if (calls.length < 3) continue;

      // Group by strike
      const byStrike = new Map<number, EnhancedPosition[]>();
      for (const call of calls) {
        const strike = this.getStrike(call);
        if (!byStrike.has(strike)) {
          byStrike.set(strike, []);
        }
        byStrike.get(strike)!.push(call);
      }

      // Look for butterfly pattern
      const strikes = Array.from(byStrike.keys()).sort((a, b) => a - b);

      for (let i = 0; i < strikes.length - 2; i++) {
        for (let j = i + 1; j < strikes.length - 1; j++) {
          for (let k = j + 1; k < strikes.length; k++) {
            const lowerStrike = strikes[i];
            const middleStrike = strikes[j];
            const upperStrike = strikes[k];

            const lowerCalls = byStrike.get(lowerStrike)!;
            const middleCalls = byStrike.get(middleStrike)!;
            const upperCalls = byStrike.get(upperStrike)!;

            // Find long call at lower strike
            const longLower = lowerCalls.find(
              (c) => c.quantity > 0 && !usedPositions.has(c.symbol),
            );
            if (!longLower) continue;

            // Find short calls at middle strike
            // Allow flexible quantity for broken wing butterflies
            const shortMiddle = middleCalls.find(
              (c) => c.quantity < 0 && !usedPositions.has(c.symbol),
            );
            if (!shortMiddle) continue;

            // Find long call at upper strike
            const longUpper = upperCalls.find(
              (c) =>
                c.quantity > 0 &&
                Math.abs(c.quantity) === Math.abs(longLower.quantity) &&
                !usedPositions.has(c.symbol),
            );
            if (!longUpper) continue;

            // Found a call butterfly!
            const legs: StrategyLeg[] = [
              {
                position: longLower,
                role: LegRole.LONG_CALL,
                strike: lowerStrike,
                optionType: "C",
                quantity: longLower.quantity,
              },
              {
                position: shortMiddle,
                role: LegRole.SHORT_CALL,
                strike: middleStrike,
                optionType: "C",
                quantity: shortMiddle.quantity,
              },
              {
                position: longUpper,
                role: LegRole.LONG_CALL,
                strike: upperStrike,
                optionType: "C",
                quantity: longUpper.quantity,
              },
            ];
            const strategyName = `${longLower["underlying-symbol"]} ${lowerStrike}/${middleStrike}/${upperStrike} Call Butterfly`;
            const metadata = this.buildStrategyMetadata({
              strategyId: `call_butterfly_${longLower.symbol}_${shortMiddle.symbol}_${longUpper.symbol}`,
              strategyName,
              strategyType: StrategyType.CALL_BUTTERFLY,
              underlyingSymbol: longLower["underlying-symbol"],
              detectionSource: "auto_detected",
              legs,
              confidence: 0.9,
            });

            const strategy: DetectedStrategy = {
              id: metadata.strategyId,
              type: StrategyType.CALL_BUTTERFLY,
              name: strategyName,
              underlyingSymbol: longLower["underlying-symbol"],
              legs,
              netPremium: this.calculateNetPremium([
                longLower,
                shortMiddle,
                longUpper,
              ]),
              netDelta:
                (longLower.positionDelta || 0) +
                (shortMiddle.positionDelta || 0) +
                (longUpper.positionDelta || 0),
              expirationDate: expiration,
              daysToExpiration: longLower.dte || 0,
              confidence: 0.9,
              metadata,
            };

            strategies.push(strategy);
            usedPositions.add(longLower.symbol);
            usedPositions.add(shortMiddle.symbol);
            usedPositions.add(longUpper.symbol);
          }
        }
      }
    }

    return strategies;
  }

  /**
   * Detect Put Butterfly strategies (3 legs, all puts)
   */
  private detectPutButterflies(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    // Group by expiration date
    const byExpiration = this.groupByExpiration(options);

    for (const [expiration, expiryOptions] of byExpiration) {
      // Filter to only puts
      const puts = expiryOptions.filter((opt) => {
        const parsed = parseOptionSymbol(opt.symbol);
        return parsed?.optionType === "P" && !usedPositions.has(opt.symbol);
      });

      if (puts.length < 3) continue;

      // Group by strike
      const byStrike = new Map<number, EnhancedPosition[]>();
      for (const put of puts) {
        const strike = this.getStrike(put);
        if (!byStrike.has(strike)) {
          byStrike.set(strike, []);
        }
        byStrike.get(strike)!.push(put);
      }

      // Look for butterfly pattern
      const strikes = Array.from(byStrike.keys()).sort((a, b) => a - b);

      for (let i = 0; i < strikes.length - 2; i++) {
        for (let j = i + 1; j < strikes.length - 1; j++) {
          for (let k = j + 1; k < strikes.length; k++) {
            const lowerStrike = strikes[i];
            const middleStrike = strikes[j];
            const upperStrike = strikes[k];

            const lowerPuts = byStrike.get(lowerStrike)!;
            const middlePuts = byStrike.get(middleStrike)!;
            const upperPuts = byStrike.get(upperStrike)!;

            // Find long put at lower strike
            const longLower = lowerPuts.find(
              (p) => p.quantity > 0 && !usedPositions.has(p.symbol),
            );
            if (!longLower) continue;

            // Find short puts at middle strike (need quantity to be 2x the wings)
            const shortMiddle = middlePuts.find(
              (p) =>
                p.quantity < 0 &&
                Math.abs(p.quantity) === Math.abs(longLower.quantity) * 2 &&
                !usedPositions.has(p.symbol),
            );
            if (!shortMiddle) continue;

            // Find long put at upper strike
            const longUpper = upperPuts.find(
              (p) =>
                p.quantity > 0 &&
                Math.abs(p.quantity) === Math.abs(longLower.quantity) &&
                !usedPositions.has(p.symbol),
            );
            if (!longUpper) continue;

            // Found a put butterfly!
            const legs: StrategyLeg[] = [
              {
                position: longLower,
                role: LegRole.LONG_PUT,
                strike: lowerStrike,
                optionType: "P",
                quantity: longLower.quantity,
              },
              {
                position: shortMiddle,
                role: LegRole.SHORT_PUT,
                strike: middleStrike,
                optionType: "P",
                quantity: shortMiddle.quantity,
              },
              {
                position: longUpper,
                role: LegRole.LONG_PUT,
                strike: upperStrike,
                optionType: "P",
                quantity: longUpper.quantity,
              },
            ];
            const strategyName = `${longLower["underlying-symbol"]} ${lowerStrike}/${middleStrike}/${upperStrike} Put Butterfly`;
            const metadata = this.buildStrategyMetadata({
              strategyId: `put_butterfly_${longLower.symbol}_${shortMiddle.symbol}_${longUpper.symbol}`,
              strategyName,
              strategyType: StrategyType.PUT_BUTTERFLY,
              underlyingSymbol: longLower["underlying-symbol"],
              detectionSource: "auto_detected",
              legs,
              confidence: 0.9,
            });

            const strategy: DetectedStrategy = {
              id: metadata.strategyId,
              type: StrategyType.PUT_BUTTERFLY,
              name: strategyName,
              underlyingSymbol: longLower["underlying-symbol"],
              legs,
              netPremium: this.calculateNetPremium([
                longLower,
                shortMiddle,
                longUpper,
              ]),
              netDelta:
                (longLower.positionDelta || 0) +
                (shortMiddle.positionDelta || 0) +
                (longUpper.positionDelta || 0),
              expirationDate: expiration,
              daysToExpiration: longLower.dte || 0,
              confidence: 0.9,
              metadata,
            };

            strategies.push(strategy);
            usedPositions.add(longLower.symbol);
            usedPositions.add(shortMiddle.symbol);
            usedPositions.add(longUpper.symbol);
          }
        }
      }
    }

    return strategies;
  }

  /**
   * Detect Ratio Spread strategies (including broken wing butterflies)
   * Pattern: Multiple long options at different strikes + short options
   */
  private detectRatioSpreads(
    options: EnhancedPosition[],
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    // Group by expiration date
    const byExpiration = this.groupByExpiration(options);

    for (const [expiration, expiryOptions] of byExpiration) {
      // Separate calls and puts
      const calls = expiryOptions.filter((opt) => {
        const parsed = parseOptionSymbol(opt.symbol);
        return parsed?.optionType === "C" && !usedPositions.has(opt.symbol);
      });
      const puts = expiryOptions.filter((opt) => {
        const parsed = parseOptionSymbol(opt.symbol);
        return parsed?.optionType === "P" && !usedPositions.has(opt.symbol);
      });

      // Check call ratio spreads
      if (calls.length >= 3) {
        const callRatios = this.detectRatioPattern(
          calls,
          "C",
          expiration,
          usedPositions,
        );
        strategies.push(...callRatios);
      }

      // Check put ratio spreads
      if (puts.length >= 3) {
        const putRatios = this.detectRatioPattern(
          puts,
          "P",
          expiration,
          usedPositions,
        );
        strategies.push(...putRatios);
      }
    }

    return strategies;
  }

  /**
   * Detect ratio spread pattern for a specific option type
   */
  private detectRatioPattern(
    options: EnhancedPosition[],
    optionType: "C" | "P",
    expiration: string,
    usedPositions: Set<string>,
  ): DetectedStrategy[] {
    const strategies: DetectedStrategy[] = [];

    // Group by quantity sign (long vs short)
    const longs = options.filter((opt) => opt.quantity > 0);
    const shorts = options.filter((opt) => opt.quantity < 0);

    console.log(`Ratio detection for ${optionType} options:`, {
      longs: longs.map((l) => ({
        symbol: l.symbol,
        qty: l.quantity,
        strike: this.getStrike(l),
      })),
      shorts: shorts.map((s) => ({
        symbol: s.symbol,
        qty: s.quantity,
        strike: this.getStrike(s),
      })),
    });

    // Look for patterns with 2+ longs and 1+ short
    if (longs.length >= 2 && shorts.length >= 1) {
      // Sort by strike
      const sortedLongs = longs.sort(
        (a, b) => this.getStrike(a) - this.getStrike(b),
      );
      const sortedShorts = shorts.sort(
        (a, b) => this.getStrike(a) - this.getStrike(b),
      );

      // Check if we have equal quantities on the long side
      const longQuantity = Math.abs(sortedLongs[0].quantity);
      const allLongsEqualQty = sortedLongs.every(
        (l) => Math.abs(l.quantity) === longQuantity,
      );

      if (allLongsEqualQty) {
        // Check if short quantity matches total long quantity
        const totalLongQty = sortedLongs.reduce(
          (sum, l) => sum + Math.abs(l.quantity),
          0,
        );
        const shortWithMatchingQty = sortedShorts.find(
          (s) =>
            Math.abs(s.quantity) === totalLongQty ||
            Math.abs(s.quantity) === longQuantity,
        );

        if (shortWithMatchingQty) {
          // Found a ratio spread pattern!
          const allLegs = [...sortedLongs, shortWithMatchingQty];
          const strikes = allLegs
            .map((l) => this.getStrike(l))
            .sort((a, b) => a - b);
          const strikeString = strikes.join("/");

          let strategyName = "";
          const underlying = sortedLongs[0]["underlying-symbol"];

          if (
            sortedLongs.length === 2 &&
            Math.abs(shortWithMatchingQty.quantity) === longQuantity
          ) {
            // This is a broken wing butterfly pattern
            strategyName = `${underlying} ${strikeString} Broken Wing ${optionType === "C" ? "Call" : "Put"} Butterfly`;
          } else {
            // General ratio spread
            strategyName = `${underlying} ${strikeString} ${optionType === "C" ? "Call" : "Put"} Ratio Spread`;
          }

          const legs: StrategyLeg[] = allLegs.map((position) => ({
            position,
            role:
              position.quantity > 0
                ? optionType === "C"
                  ? LegRole.LONG_CALL
                  : LegRole.LONG_PUT
                : optionType === "C"
                  ? LegRole.SHORT_CALL
                  : LegRole.SHORT_PUT,
            strike: this.getStrike(position),
            optionType,
            quantity: position.quantity,
          }));
          const strategyId = `ratio_${allLegs.map((l) => l.symbol).join("_")}`;
          const metadata = this.buildStrategyMetadata({
            strategyId,
            strategyName,
            strategyType: StrategyType.RATIO_SPREAD,
            underlyingSymbol: underlying,
            detectionSource: "auto_detected",
            legs,
            confidence: 0.85,
          });

          const strategy: DetectedStrategy = {
            id: strategyId,
            type: StrategyType.RATIO_SPREAD,
            name: strategyName,
            underlyingSymbol: underlying,
            legs,
            netPremium: this.calculateNetPremium(allLegs),
            netDelta: allLegs.reduce(
              (sum, pos) => sum + (pos.positionDelta || 0),
              0,
            ),
            expirationDate: expiration,
            daysToExpiration: sortedLongs[0].dte || 0,
            confidence: 0.85,
            metadata,
          };

          strategies.push(strategy);
          allLegs.forEach((leg) => usedPositions.add(leg.symbol));
        }
      }
    }

    return strategies;
  }

  /**
   * Detect strategies from position attributes (tagged by strategy metadata)
   */
  private detectTaggedStrategies(
    positions: EnhancedPosition[],
    positionAttributes?: Map<string, PositionAttributeEntry[]>,
  ): DetectedStrategy[] {
    if (!positionAttributes || positionAttributes.size === 0) {
      return [];
    }

    // Group positions by strategy_id attribute
    const strategyGroups = new Map<
      string,
      {
        positions: EnhancedPosition[];
        strategySource?: string;
        strategyName?: string;
      }
    >();

    for (const position of positions) {
      const posKey = `${position["account-number"]}_${position.symbol}`;
      const attrs = positionAttributes.get(posKey);

      if (attrs && attrs.length) {
        const strategyIdAttr = attrs.find(
          (attribute) => attribute.attribute_type === "strategy_id",
        );
        const strategySourceAttr = attrs.find(
          (attribute) => attribute.attribute_type === "strategy_source",
        );
        const strategyNameAttr = attrs.find(
          (attribute) => attribute.attribute_type === "strategy_name",
        );

        if (strategyIdAttr?.attribute_value) {
          const strategyId = String(strategyIdAttr.attribute_value);
          const strategySourceValue = strategySourceAttr?.attribute_value;
          const sourceKey = String(
            strategySourceValue ?? "unknown",
          );
          const key = `${sourceKey}_${strategyId}`;

          if (!strategyGroups.has(key)) {
            strategyGroups.set(key, {
              positions: [],
              strategySource: strategySourceValue
                ? String(strategySourceValue)
                : undefined,
              strategyName: strategyNameAttr?.attribute_value
                ? String(strategyNameAttr.attribute_value)
                : undefined,
            });
          }

          strategyGroups.get(key)!.positions.push(position);
        }
      }
    }

    // Create detected strategies from groups
    const strategies: DetectedStrategy[] = [];

    for (const [key, group] of strategyGroups) {
      if (group.positions.length === 0) continue;

      const [source, strategyId] = key.split("_", 2);
      const strategy = this.createStrategyFromTaggedPositions(
        group.positions,
        strategyId,
        group.strategyName || "Unknown Strategy",
        group.strategySource,
        source,
      );

      strategies.push(strategy);
    }

    console.log(
      `Strategy Detector: Found ${strategies.length} tagged strategies`,
    );

    return strategies;
  }

  /**
   * Create a strategy from tagged positions
   */
  private createStrategyFromTaggedPositions(
    positions: EnhancedPosition[],
    strategyId: string,
    strategyName: string,
    strategySource?: string,
    sourceHint?: string,
  ): DetectedStrategy {
    // Sort positions by symbol for consistent ordering
    const sortedPositions = [...positions].sort((a, b) =>
      a.symbol.localeCompare(b.symbol),
    );

    const underlyingSymbol = positions[0]?.["underlying-symbol"] ?? "Unknown";
    const mappedStrategySource = this.mapStrategySource(
      strategySource ?? sourceHint,
    );
    const legs = sortedPositions.map((pos) => ({
      position: pos,
      role: this.inferLegRole(pos),
      strike: parseOptionSymbol(pos.symbol)?.strike || 0,
      optionType: parseOptionSymbol(pos.symbol)?.optionType || ("C" as const),
      quantity: pos.quantity,
    }));

    // Calculate strategy metrics
    const netPremium = positions.reduce((sum, p) => {
      const price = parseFloat(String(p["average-open-price"] ?? 0)) || 0;
      const multiplier = p.multiplier || 100;
      return sum + price * p.quantity * multiplier;
    }, 0);

    const netDelta = positions.reduce(
      (sum, p) => sum + (p.positionDelta || 0),
      0,
    );

    // Get expiration dates
    const expirationDates = positions
      .map((p) => parseOptionSymbol(p.symbol)?.expiry)
      .filter(Boolean) as string[];
    const earliestExpiry = expirationDates.sort()[0] || "";

    const detectedType = this.inferStrategyTypeFromLegs(legs);
    const metadata = this.buildStrategyMetadata({
      strategyId: `tagged_${strategyId}`,
      strategyName,
      strategyType: detectedType,
      underlyingSymbol,
      detectionSource: "tagged",
      legs,
      confidence: 1,
      sourceStrategyId: strategyId,
      sourceStrategyName: strategyName,
      mappedStrategySource,
    });

    return {
      id: metadata.strategyId,
      type: detectedType,
      name: strategyName,
      underlyingSymbol,
      legs,
      netPremium,
      netDelta,
      expirationDate: earliestExpiry,
      daysToExpiration: Math.min(...positions.map((p) => p.dte || 999)),
      confidence: 1.0, // Tagged strategies have 100% confidence
      metadata,
    };
  }

  /**
   * Get positions not used in any strategies
   */
  private getUnusedPositions(
    allPositions: EnhancedPosition[],
    strategies: DetectedStrategy[],
  ): EnhancedPosition[] {
    const usedSymbols = new Set(
      strategies.flatMap((s) => s.legs.map((l) => l.position.symbol)),
    );

    return allPositions.filter((p) => !usedSymbols.has(p.symbol));
  }

  /**
   * Infer strategy type from leg configuration
   */
  private inferStrategyTypeFromLegs(legs: StrategyLeg[]): StrategyKind {
    const optionLegs = legs.filter(
      (leg) => leg.position["instrument-type"] === "Equity Option",
    );

    if (optionLegs.length === 0) {
      return StrategyType.SINGLE_LEG;
    }

    if (optionLegs.length === 2) {
      const [leg1, leg2] = optionLegs;
      const sameExpiry =
        parseOptionSymbol(leg1.position.symbol)?.expiry ===
        parseOptionSymbol(leg2.position.symbol)?.expiry;

      if (sameExpiry) {
        return StrategyType.VERTICAL_SPREAD;
      } else {
        return StrategyType.CALENDAR_SPREAD;
      }
    }

    if (optionLegs.length === 4) {
      // Could be iron condor or butterfly - need more logic here
      return StrategyType.IRON_CONDOR;
    }

    return StrategyType.CUSTOM;
  }

  private inferLegRole(position: EnhancedPosition): LegRoleValue {
    const quantity = position.quantity ?? 0;
    const instrumentType = position["instrument-type"];
    if (instrumentType === "Equity" || instrumentType === "ETF") {
      return quantity >= 0 ? LegRole.LONG_STOCK : LegRole.SHORT_STOCK;
    }

    const parsed = parseOptionSymbol(position.symbol);
    if (parsed?.optionType === "C") {
      return quantity >= 0 ? LegRole.LONG_CALL : LegRole.SHORT_CALL;
    }

    if (parsed?.optionType === "P") {
      return quantity >= 0 ? LegRole.LONG_PUT : LegRole.SHORT_PUT;
    }

    return quantity >= 0 ? LegRole.LONG_CALL : LegRole.SHORT_CALL;
  }

  private buildStrategyMetadata(options: {
    strategyId: string;
    strategyName: string;
    strategyType: StrategyKind;
    underlyingSymbol: string;
    detectionSource: StrategyDetectionSource;
    legs: StrategyLeg[];
    confidence: number;
    complexOrderId?: string;
    orderDescription?: string;
    sourceStrategyId?: string;
    sourceStrategyName?: string;
    mappedStrategySource?: StrategySource;
    isUserModified?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
  }): DetectedStrategyMetadata {
    const {
      strategyId,
      strategyName,
      strategyType,
      underlyingSymbol,
      detectionSource,
      legs,
      confidence,
      complexOrderId,
      orderDescription,
      sourceStrategyId,
      sourceStrategyName,
      mappedStrategySource,
      isUserModified = false,
      createdAt,
      updatedAt,
    } = options;

    const now = new Date();
    const created = createdAt ?? now;
    const updated = updatedAt ?? now;
    const targetQuantity = this.calculateTargetQuantity(legs);

    const additionalData: Record<string, unknown> = {
      detectionConfidence: confidence,
    };

    if (complexOrderId) {
      additionalData.complexOrderId = complexOrderId;
    }
    if (orderDescription) {
      additionalData.orderDescription = orderDescription;
    }
    if (sourceStrategyId) {
      additionalData.sourceStrategyId = sourceStrategyId;
    }
    if (sourceStrategyName) {
      additionalData.sourceStrategyName = sourceStrategyName;
    }

    return {
      strategyId,
      strategyName,
      strategyType,
      strategySource:
        mappedStrategySource ?? this.resolveStrategySource(detectionSource),
      symbol: underlyingSymbol,
      underlyingSymbol,
      targetQuantity,
      strategyCategory: this.resolveStrategyCategory(strategyType, legs),
      detectionSource,
      isUserModified,
      complexOrderId,
      orderDescription,
      sourceStrategyId,
      sourceStrategyName,
      detectionConfidence: confidence,
      createdAt: created,
      updatedAt: updated,
      additionalData,
    };
  }

  private createDetectedStrategy(options: {
    id: string;
    type: StrategyKind;
    name: string;
    underlyingSymbol: string;
    legs: StrategyLeg[];
    positions: EnhancedPosition[];
    detectionSource: StrategyDetectionSource;
    confidence: number;
    maxProfit?: number;
    maxLoss?: number;
    breakevens?: number[];
    complexOrderId?: string;
    orderDescription?: string;
    sourceStrategyId?: string;
    sourceStrategyName?: string;
    mappedStrategySource?: StrategySource;
    createdAt?: Date;
    updatedAt?: Date;
  }): DetectedStrategy {
    const {
      id,
      type,
      name,
      underlyingSymbol,
      legs,
      positions,
      detectionSource,
      confidence,
      maxProfit,
      maxLoss,
      breakevens,
      complexOrderId,
      orderDescription,
      sourceStrategyId,
      sourceStrategyName,
      mappedStrategySource,
      createdAt,
      updatedAt,
    } = options;

    const metadata = this.buildStrategyMetadata({
      strategyId: id,
      strategyName: name,
      strategyType: type,
      underlyingSymbol,
      detectionSource,
      legs,
      confidence,
      complexOrderId,
      orderDescription,
      sourceStrategyId,
      sourceStrategyName,
      mappedStrategySource,
      createdAt,
      updatedAt,
    });

    return {
      id,
      type,
      name,
      underlyingSymbol,
      legs,
      netPremium: this.calculateNetPremium(positions),
      netDelta: this.calculateNetDelta(positions),
      expirationDate: this.getEarliestExpiration(positions),
      daysToExpiration: this.calculateDaysToExpiration(positions),
      confidence,
      maxProfit,
      maxLoss,
      breakevens,
      metadata,
    };
  }

  private calculateTargetQuantity(legs: StrategyLeg[]): number {
    if (!legs.length) {
      return 0;
    }

    const quantities = legs
      .map((leg) => Math.abs(leg.quantity))
      .filter((quantity) => Number.isFinite(quantity));

    return quantities.length ? Math.max(...quantities) : 0;
  }

  private resolveStrategyCategory(
    strategyType: StrategyKind,
    legs: StrategyLeg[],
  ): StrategyCategory {
    switch (strategyType) {
      case StrategyType.COVERED_CALL:
      case StrategyType.CASH_SECURED_PUT:
      case StrategyType.COLLAR:
        return "income";
      case StrategyType.IRON_CONDOR:
      case StrategyType.IRON_BUTTERFLY:
      case StrategyType.CALL_BUTTERFLY:
      case StrategyType.PUT_BUTTERFLY:
      case StrategyType.VERTICAL_SPREAD:
      case StrategyType.CALENDAR_SPREAD:
      case StrategyType.DIAGONAL_SPREAD:
      case StrategyType.BUTTERFLY:
      case StrategyType.CONDOR:
        return "defined_risk";
      case StrategyType.STRADDLE:
      case StrategyType.STRANGLE:
      case StrategyType.RATIO_SPREAD:
        return "undefined_risk";
      case StrategyType.SINGLE_LEG: {
        const hasEquityLeg = legs.some(
          (leg) => leg.position["instrument-type"] === "Equity",
        );
        return hasEquityLeg ? "stock" : "custom";
      }
      default:
        return "custom";
    }
  }

  private resolveStrategySource(
    detectionSource: StrategyDetectionSource,
  ): StrategySource {
    switch (detectionSource) {
      case "complex_order":
        return "imported";
      case "user_defined":
      case "tagged":
        return "manual";
      case "auto_detected":
      default:
        return "custom";
    }
  }

  private mapStrategySource(value?: string): StrategySource | undefined {
    if (!value) return undefined;
    const allowed: StrategySource[] = [
      "core",
      "custom",
      "0dte",
      "manual",
      "imported",
    ];

    return allowed.includes(value as StrategySource)
      ? (value as StrategySource)
      : undefined;
  }
}

// Export singleton instance
export const positionStrategyDetector = new PositionStrategyDetector();
